import React, { forwardRef, useMemo, useState } from 'react';

import {
  Input as ChakraInput,
  InputProps,
  FlexProps,
  Flex,
  InputGroup,
  Button,
  InputRightElement,
  InputLeftElement,
  Link,
  Spacer,
} from '@chakra-ui/react';
import { useFormState } from 'react-hook-form';

import { Icon } from 'components/icon';
import { inputStyle } from 'constants/inputStyles';
import { handleObjectString } from 'utils/parsers';

import HelperMessage from './helper';
import Label from './label';

interface TextInputProps extends InputProps {
  helper?: string
  leftIcon?: string
  isSecure?: boolean
  withSecureHelper?: boolean
  viewProps?: FlexProps
  label?: string
  status?:'default' | 'success' | 'error'
  tooltip?: any
  RightElement?: React.FC
  LeftElement?: React.FC
}

const Input = forwardRef(({
  name,
  label,
  isSecure,
  withSecureHelper = false,
  isRequired,
  isDisabled,
  helper,
  status = 'default',
  tooltip,
  leftIcon,
  placeholder,
  viewProps = {},
  RightElement,
  LeftElement,
  ...rest
}: TextInputProps, ref: any) => {
  const [show, setShow] = useState(isSecure);
  const { errors } = useFormState({
    name,
  });
  const error = handleObjectString(errors, name);
  const currentStatus = useMemo(() => (error ? 'error' : status), [error]);
  const {
    borderColor,
    helperColor,
    icon,
  } = useMemo(() => inputStyle[isDisabled ? 'disabled' : currentStatus], [isDisabled, currentStatus]);
  function TogglePasswordVisibility() {
    if (!isSecure) {
      return null;
    }
    return (
      <InputRightElement>
        <Button
          borderLeft="1px"
          borderRadius="0"
          borderColor="gray.400"
          variant="unstyled"
          display="flex"
          align="center"
          justify="center"
          onClick={() => setShow(!show)}>
          <Icon
            size="md"
            color="gray.400"
            family="fal"
            name="eye"
          />
        </Button>
      </InputRightElement>
    );
  }

  return (
    <Flex
      alignSelf="flex-start"
      width="100%"
      flexDir="column"
      {...viewProps}>
      <Label
        isDisabled={isDisabled}
        label={label}
        isRequired={isRequired}
        tooltip={tooltip}
      />
      <InputGroup>
        {(!!leftIcon || isSecure) && (
          <InputLeftElement>
            <Icon
              color="gray.400"
              family={isSecure ? 'far' : 'fas'}
              name={leftIcon || 'key'}
            />
          </InputLeftElement>
        )}
        {!!LeftElement && (
        <InputLeftElement
          mr={isSecure ? 10 : 0}>
          <LeftElement />
        </InputLeftElement>
        )}
        <ChakraInput
          bg="white.default"
          display="flex"
          align="center"
          isRequired={isRequired}
          justify="center"
          borderRadius={4}
          type={show ? 'password' : 'text'}
          _hover={null}
          _focus={{
            boxShadow: 's1',
            borderColor: currentStatus !== 'default' ? borderColor : 'primary.default',
          }}
          _disabled={{
            bg: 'gray.50',
            cursor: 'not-allowed',
          }}
          isDisabled={isDisabled}
          placeholder={placeholder}
          borderColor={borderColor}
          name={name}
          ref={ref}
          {...rest}
        />
        {!!RightElement && (
        <InputRightElement
          mr={isSecure ? 10 : 0}>
          <RightElement />
        </InputRightElement>
        )}
        {!!icon && (
        <InputRightElement mr={isSecure ? 10 : 0}>
          <Icon
            color={helperColor}
            family="fal"
            size="md"
            name={icon}
          />
        </InputRightElement>
        )}
        <TogglePasswordVisibility />
      </InputGroup>
      <Flex
        dir="row">
        <HelperMessage
          color={helperColor}
          helper={error?.message ?? helper}
        />
        <Spacer />
        {!!isSecure && withSecureHelper && (
        <Link
          href="/passwordrecovery">
          <HelperMessage
            color="gray.400"
            helper="Esqueci minha senha"
          />
        </Link>
        )}
      </Flex>
    </Flex>
  );
});

export default Input;
